import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import harmonaiLogo from "@/assets/harmonyai-logo.svg";
import LanguageThemeSwitcher from "@/components/ui/language-theme-switcher";
import { AuthBreakout } from "@/components/auth/breakout/auth-breakout";

/**
 * Misafir auth sahnesi. Solda marka, sağda yüzen cam kart.
 * Boyutlar vh ile sıkışır; kısa ekranda panel tam boyuta yayılmaz.
 * Login'de sol kolonun ızgarası ile marka arasına breakout oturur.
 */
const AUTH_GRID =
  "lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(440px,560px)]";

const LOGO_HOLD_MS = 5000;

const AuthAmbient = () => (
  <>
    <style>{`
      @keyframes auth-drift-a {
        0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
        50% { transform: translate3d(8%, 6%, 0) scale(1.08); }
      }
      @keyframes auth-drift-b {
        0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
        50% { transform: translate3d(-7%, -8%, 0) scale(1.1); }
      }
      .auth-stage {
        --auth-stack-gap: clamp(0.85rem, 1.7vh, 1.5rem);
        --auth-form-gap: clamp(0.7rem, 1.35vh, 1.1rem);
        --auth-social-gap: clamp(0.4rem, 0.85vh, 0.625rem);
        --auth-footer-gap: clamp(0.65rem, 1.2vh, 0.95rem);
        --auth-title-size: clamp(1.25rem, 2.2vh, 1.625rem);
        --auth-btn-h: clamp(2.125rem, 4vh, 2.5rem);
        --auth-field-h: clamp(2.125rem, 4vh, 2.5rem);
        --auth-card-pad: clamp(1rem, 1.8vh, 1.5rem);
        --auth-brand-logo: clamp(2.5rem, 5.2vh, 4rem);
        --auth-brand-title: clamp(1.75rem, 2.2vh + 1.6vw, 3rem);
        --auth-brand-lead: clamp(0.875rem, 1.45vh, 1rem);
        --auth-brand-gap: clamp(0.75rem, 1.5vh, 1.5rem);
        --auth-aside-pad: clamp(1.5rem, 3.2vh, 3.5rem);
        --auth-grid-size: clamp(2.25rem, 5.2vh, 4rem);
      }
      .auth-stage .auth-grid {
        background-size: var(--auth-grid-size) var(--auth-grid-size);
      }
      @media (max-width: 1023px) {
        .auth-stage {
          --auth-stack-gap: clamp(0.75rem, 1.6vh, 1.1rem);
          --auth-form-gap: clamp(0.65rem, 1.2vh, 0.9rem);
          --auth-social-gap: 0.4rem;
          --auth-footer-gap: 0.7rem;
          --auth-title-size: 1.25rem;
          --auth-btn-h: 2.125rem;
          --auth-field-h: 2.125rem;
          --auth-card-pad: 1rem;
          --auth-grid-size: clamp(2rem, 6vw, 2.75rem);
        }
      }
      @keyframes auth-logo-glow {
        0%, 100% { transform: scale(1.35); opacity: 0.45; }
        50% { transform: scale(1.85); opacity: 0.95; }
      }
      @keyframes auth-flare-spin {
        to { transform: rotate(360deg); }
      }
      .auth-logo-glow {
        background: radial-gradient(circle, rgba(244,214,255,0.95) 0%, rgba(167,139,250,0.55) 38%, rgba(192,132,252,0.15) 58%, transparent 72%);
        filter: blur(8px);
        animation: auth-logo-glow 2.6s ease-in-out infinite;
      }
      .auth-logo-ring {
        background: radial-gradient(circle, transparent 52%, rgba(196,181,253,0.28) 60%, rgba(255,255,255,0.55) 66%, rgba(139,92,246,0.35) 74%, transparent 84%);
        filter: blur(1px);
      }
      .auth-logo-flare {
        background: conic-gradient(
          from 0deg,
          transparent 0deg,
          transparent 210deg,
          rgba(196,181,253,0.2) 255deg,
          rgba(255,255,255,0.98) 305deg,
          rgba(192,132,252,0.75) 338deg,
          transparent 360deg
        );
        -webkit-mask: radial-gradient(circle, transparent 52%, #000 58%, #000 76%, transparent 84%);
        mask: radial-gradient(circle, transparent 52%, #000 58%, #000 76%, transparent 84%);
        filter: drop-shadow(0 0 14px rgba(196,181,253,0.95));
        animation: auth-flare-spin 0.85s linear infinite;
      }
      @media (prefers-reduced-motion: reduce) {
        .auth-orb, .auth-logo-glow, .auth-logo-flare { animation: none !important; }
      }
      .auth-left, .auth-left * { pointer-events: none; }
      .auth-left .auth-hit, .auth-left .auth-hit * { pointer-events: auto; }
    `}</style>
    <div
      aria-hidden
      className="auth-orb pointer-events-none absolute -top-32 -left-24 h-[48vh] w-[48vh] rounded-full bg-indigo-300/40 blur-[100px] dark:bg-indigo-500/25"
      style={{ animation: "auth-drift-a 18s ease-in-out infinite" }}
    />
    <div
      aria-hidden
      className="auth-orb pointer-events-none absolute -bottom-24 -right-16 h-[52vh] w-[52vh] rounded-full bg-violet-300/35 blur-[110px] dark:bg-fuchsia-500/20"
      style={{ animation: "auth-drift-b 22s ease-in-out infinite" }}
    />
  </>
);

export const AuthStage = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const showBreakout = pathname === "/login";
  const [breakoutOn, setBreakoutOn] = useState(false);
  const [holdRatio, setHoldRatio] = useState(0);
  const [holdCount, setHoldCount] = useState(5);
  const holdFrame = useRef(0);

  useEffect(() => () => cancelAnimationFrame(holdFrame.current), []);

  const stopHold = () => {
    cancelAnimationFrame(holdFrame.current);
    setHoldRatio(0);
  };

  const beginHold = (event: PointerEvent<HTMLButtonElement>) => {
    if (!showBreakout || breakoutOn) return;
    cancelAnimationFrame(holdFrame.current);
    event.currentTarget.setPointerCapture(event.pointerId);
    const started = performance.now();
    const tick = (now: number) => {
      const elapsed = now - started;
      if (elapsed >= LOGO_HOLD_MS) {
        setHoldRatio(0);
        setBreakoutOn(true);
        return;
      }
      setHoldRatio(elapsed / LOGO_HOLD_MS);
      setHoldCount(Math.ceil((LOGO_HOLD_MS - elapsed) / 1000));
      holdFrame.current = requestAnimationFrame(tick);
    };
    holdFrame.current = requestAnimationFrame(tick);
  };

  return (
    <div className="auth-stage fixed inset-0 h-[100dvh] overflow-hidden bg-bg-quaternary text-text-primary dark:bg-background dark:text-foreground">
      <AuthAmbient />
      <div className="auth-grid pointer-events-none absolute inset-0 opacity-40 bg-grid-theme dark:opacity-50" />

      {showBreakout && breakoutOn && (
        <div className={`pointer-events-none absolute inset-0 z-[1] hidden ${AUTH_GRID}`}>
          <AuthBreakout />
        </div>
      )}

      <div className="lg:hidden absolute inset-x-0 top-0 z-20 flex items-center justify-between px-4 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <img
          src={harmonaiLogo}
          alt="HarmonyAI"
          className="h-7 w-7 object-contain select-none"
        />
        <LanguageThemeSwitcher />
      </div>

      <div className={`pointer-events-none relative z-10 flex h-full min-h-0 ${AUTH_GRID}`}>
        <aside className="auth-left relative hidden lg:flex flex-col justify-between p-[var(--auth-aside-pad)]">
          {breakoutOn && (
            <div
              aria-hidden
              className="pointer-events-none absolute left-1/2 top-[46%] h-[22rem] w-[min(100%,28rem)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-background/30 blur-3xl"
            />
          )}
          <div />
          <div className="relative z-[1] max-w-md">
            <button
              type="button"
              className="auth-hit relative block cursor-pointer touch-none appearance-none border-0 bg-transparent p-0 select-none"
              style={{
                width: "var(--auth-brand-logo)",
                height: "var(--auth-brand-logo)",
                marginBottom: "var(--auth-brand-gap)",
              }}
              aria-label={t("auth.breakout.hold")}
              onPointerDown={beginHold}
              onPointerUp={stopHold}
              onPointerCancel={stopHold}
              onContextMenu={(event) => event.preventDefault()}
            >
              <span
                aria-hidden
                className="auth-logo-glow pointer-events-none absolute inset-0 rounded-full"
              />
              {holdRatio > 0 && (
                <>
                  <span
                    aria-hidden
                    className="auth-logo-ring pointer-events-none absolute -inset-[42%] rounded-full"
                  />
                  <span
                    aria-hidden
                    className="auth-logo-flare pointer-events-none absolute -inset-[42%] rounded-full"
                  />
                  <span className="pointer-events-none absolute left-1/2 top-1/2 z-[2] flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-lg font-semibold tabular-nums text-violet-700 shadow-[0_0_22px_rgba(139,92,246,0.85)] dark:bg-gray-950/75 dark:text-violet-100">
                    {holdCount}
                  </span>
                </>
              )}
              <img
                src={harmonaiLogo}
                alt=""
                draggable={false}
                className="relative z-[1] h-full w-full object-contain"
              />
            </button>
            <h2
              className={`font-semibold tracking-tight text-text-primary dark:text-gray-100 ${
                breakoutOn
                  ? "drop-shadow-[0_1px_14px_rgba(255,255,255,0.82)] dark:drop-shadow-[0_2px_18px_rgba(0,0,0,0.72)]"
                  : ""
              }`}
              style={{
                fontSize: "var(--auth-brand-title)",
                lineHeight: 1.15,
              }}
            >
              {t("auth.welcome")}
            </h2>
            <p
              className={`text-muted-foreground ${
                breakoutOn
                  ? "drop-shadow-[0_1px_10px_rgba(255,255,255,0.75)] dark:drop-shadow-[0_2px_14px_rgba(0,0,0,0.65)]"
                  : ""
              }`}
              style={{
                marginTop: "var(--auth-brand-gap)",
                fontSize: "var(--auth-brand-lead)",
                lineHeight: 1.55,
              }}
            >
              {t("auth.stageTagline")}
            </p>
          </div>
          <div className="auth-hit relative z-[1] w-full">
            <LanguageThemeSwitcher />
          </div>
        </aside>

        <div className="pointer-events-auto flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
          <div className="flex min-h-full flex-1 items-center justify-center px-4 pt-[max(4.25rem,calc(env(safe-area-inset-top)+2.75rem))] pb-[max(1rem,env(safe-area-inset-bottom))] lg:px-5 lg:py-6">
            <div className="auth-panel w-full max-w-[360px] rounded-3xl border border-black/[0.05] bg-white/45 p-[var(--auth-card-pad)] shadow-[0_12px_36px_-20px_rgba(15,23,42,0.14)] backdrop-blur-2xl dark:border-white/10 dark:bg-gray-950/45 dark:shadow-[0_24px_80px_-24px_rgba(0,0,0,0.45)]">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
